/**
 * A kitchen being drawn: the file, as text, plus the four operations a painter
 * needs on it.
 *
 * The state the page holds is the *file*, not a grid of station types. Two
 * reasons, and both of them are about there being one format rather than two:
 * the directive lines (`// orders:`, `// container:`) are content and have to
 * survive every edit, and what the editor downloads has to be the same bytes
 * `npm run map -- validate` reads. A draft that lived as a parsed `MapContent`
 * would need serialising on the way out and would lose anything the parser
 * ignores on the way in.
 *
 * Nothing here knows a rule. Whether a layout is playable is `validateMap` and
 * `mapReport`'s answer, in `checkDraft` below, and both come from
 * `@overcooked/engine` — the same source the server boots on, because
 * `vite.config.ts` aliases the package to its TypeScript rather than rebuilding
 * it for the browser.
 */

import {
  CRATE_CONTENTS,
  ContentError,
  MAP_LEGEND,
  MAX_MAP_SIDE,
  MIN_MAP_SIDE,
  mapReport,
  parseMap,
  saveBlocking,
  validateMap,
} from '@overcooked/engine'
import type { Content, EditorContent, MapContent, MapReport } from '@overcooked/engine'

/** `/editor`. One page, no session id — a draft is not addressable. */
export const EDITOR_PATH = /^\/editor\/?$/

/**
 * What the server hands over so the browser can validate against real content.
 *
 * The engine's, not a copy: `server/src/content/editorRoutes.ts` builds the
 * payload against the same declaration, so a field renamed on one end stops the
 * other end compiling rather than emptying a checklist in somebody's tab.
 */
export type { EditorContent }

/** `plate_stack` -> `plate stack`: a station type as the palette and grid say it. */
export const tileWords = (type: string): string => type.replace(/_/g, ' ')

/** One paintable glyph, and what it puts in the kitchen. */
export interface PaletteEntry {
  glyph: string
  label: string
}

/**
 * The palette, derived from the legend rather than written out.
 *
 * A glyph added to `MAP_LEGEND` appears here without anybody remembering to add
 * it, which is the same reason the CLI's legend is derived and the HUD's
 * ingredient icons are: the map format has exactly one definition.
 */
export const PALETTE: PaletteEntry[] = [
  ...Object.entries(MAP_LEGEND).map(([glyph, type]) => ({
    glyph,
    label: CRATE_CONTENTS[glyph] ? `${tileWords(type)} · ${CRATE_CONTENTS[glyph]}` : tileWords(type),
  })),
  { glyph: '1', label: 'chef 1 start' },
  { glyph: '2', label: 'chef 2 start' },
  { glyph: '3', label: 'chef 3 start' },
]

const SPAWN = /^[1-9]$/

/** The file, split into the lines that are a picture and the lines that are not. */
export interface Draft {
  /** Comment lines, in their original order. Directives live here. */
  directives: string[]
  rows: string[][]
}

export function parseDraft(text: string): Draft {
  const lines = text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
  return {
    directives: lines.filter((line) => line.startsWith('//')),
    rows: lines.filter((line) => !line.startsWith('//')).map((line) => line.split(/\s+/)),
  }
}

/** Directives first, then the grid — the shape every shipped preset is written in. */
export function draftText(draft: Draft): string {
  return [...draft.directives, ...draft.rows.map((row) => row.join(' '))].join('\n') + '\n'
}

/**
 * Put one glyph in one cell.
 *
 * Painting a seat digit lifts that digit from wherever it was, because a map
 * with two `1`s is not a map and answering a click with a parse error would be
 * the editor refusing to do the obvious thing. Every other glyph is painted
 * where it was clicked and nowhere else.
 */
export function paint(draft: Draft, x: number, y: number, glyph: string): Draft {
  const rows = draft.rows.map((row) => [...row])
  if (!rows[y] || rows[y][x] === undefined) return draft
  if (SPAWN.test(glyph)) {
    rows.forEach((row, ry) => {
      row.forEach((cell, rx) => {
        if (cell === glyph && !(rx === x && ry === y)) rows[ry][rx] = '.'
      })
    })
  }
  rows[y][x] = glyph
  return { ...draft, rows }
}

/**
 * Grow or shrink the grid.
 *
 * New cells are walls rather than floor: a kitchen is a room with a wall around
 * it, and a new column of floor along the edge would leave every draft failing
 * the "no station any seat can face" check while it was being drawn.
 *
 * The bounds are the engine's (`MIN_MAP_SIDE` / `MAX_MAP_SIDE`) rather than two
 * numbers of this widget's own. A stepper that stops one tile before the parser
 * does is a stepper that draws a kitchen the parser then refuses, and once the
 * draft can be posted to a server the only bound that means anything is the one
 * `parseMap` enforces.
 */
export function resize(draft: Draft, width: number, height: number): Draft {
  const w = Math.max(MIN_MAP_SIDE, Math.min(MAX_MAP_SIDE, width))
  const h = Math.max(MIN_MAP_SIDE, Math.min(MAX_MAP_SIDE, height))
  const rows: string[][] = []
  for (let y = 0; y < h; y += 1) {
    const row: string[] = []
    for (let x = 0; x < w; x += 1) row.push(draft.rows[y]?.[x] ?? 'W')
    rows.push(row)
  }
  return { ...draft, rows }
}

/** What the checklist shows: the report's rules, plus the boot path's verdict. */
export interface DraftCheck {
  rule: string
  ok: boolean
  detail: string
  /** True when failing this rule is advice about the kitchen, not a refusal to start. */
  advisory: boolean
  /**
   * True for a rule that refuses a save but not a boot — the item-flow proof.
   * Failing it turns the verdict red and switches Save off, while
   * `refusedBecause` stays null: the server would start on it.
   */
  saveOnly?: boolean
}

export interface DraftVerdict {
  /** Null when the file does not parse — then `checks` is the parse error alone. */
  report: MapReport | null
  /** Every rule, advisory ones included, in the order a designer reads them. */
  checks: DraftCheck[]
  /** `validateMap`'s message, or null when it accepted the draft. */
  refusedBecause: string | null
  /**
   * May this file be saved — would the server start on it, and can every dish
   * be carried crate to hatch?
   *
   * `validateMap` is the only authority on booting, so this is its verdict, the
   * boot-blocking rules that must agree with it, and the save-only item-flow
   * row — never an advisory rule. A kitchen whose two seats stand in one region
   * boots and saves; saying "this kitchen has problems" about it would be the
   * page's one sentence being false.
   */
  ok: boolean
  /**
   * The save-only rows that fail, when nothing else does: the server would
   * start on this file, and it still cannot be saved. Empty otherwise.
   */
  saveRefusals: DraftCheck[]
  /**
   * The rules that are worth saying and are not reasons to stop. Non-empty with
   * `ok` true is the normal, meaningful case: a playable kitchen, drawn in a way
   * the study would not learn anything from.
   */
  advice: DraftCheck[]
}

/**
 * Every rule, against the real recipe book.
 *
 * `validateContent` is deliberately *not* run: it validates the recipe book and
 * the knobs, which this page cannot edit and the server already refused to boot
 * without. What it needs — and all it needs — is `validateMap`, which asks the
 * book what a dish requires and the knobs how much a plate holds.
 */
export function checkDraft(text: string, name: string, base: EditorContent): DraftVerdict {
  let map: MapContent
  try {
    map = parseMap(text, name)
  } catch (err) {
    const message = err instanceof ContentError ? err.message : String(err)
    return {
      report: null,
      checks: [{ rule: 'the file parses', ok: false, detail: message, advisory: false }],
      refusedBecause: message,
      ok: false,
      advice: [],
      saveRefusals: [],
    }
  }

  // The bundle the server builds for the same call, minus the clocks it is not
  // allowed to send: `validateMap` reads `plateCapacity`, and the order clocks
  // when the draft carries a `// schedule:` (the rail check), and nothing else.
  const content = {
    map,
    maps: { [name]: map },
    recipes: base.recipes,
    timings: { plateCapacity: base.plateCapacity, ...base.orderClock },
  } as unknown as Content

  const report = mapReport(content, map)
  let refusedBecause: string | null = null
  try {
    validateMap(content, map)
  } catch (err) {
    refusedBecause = err instanceof ContentError ? err.message : String(err)
  }

  return {
    report,
    checks: report.checks,
    refusedBecause,
    // The save verdict: the boot rules — which must agree with `validateMap`,
    // and a disagreement is a bug in the engine rather than something this page
    // should resolve quietly, so both are consulted — plus the save-only
    // item-flow row. `saveBlocking` drops only the advice.
    ok: refusedBecause === null && saveBlocking(report.checks).every((c) => c.ok),
    advice: report.checks.filter((c) => c.advisory && !c.ok),
    saveRefusals: report.checks.filter((c) => c.saveOnly && !c.ok),
  }
}

/**
 * Hand the draft to the browser as a file.
 *
 * Three things this does that the obvious four lines do not, and each of them is
 * a browser that would otherwise save nothing:
 *
 *   - the anchor is **in the document** when it is clicked. A detached `<a>` is
 *     not a click target in Firefox, so `a.click()` on one is a no-op there;
 *   - the object URL is revoked on a **later tick**. Revoking it synchronously
 *     races the download Safari has not started yet, and the file arrives empty;
 *   - the `document` is a parameter, which is what lets this be tested at all —
 *     the bug it replaces was invisible to every test because it lived in a
 *     callback that only a real browser ever ran.
 */
export function downloadText(
  text: string,
  filename: string,
  doc: Document = document,
  later: (fn: () => void) => void = (fn) => setTimeout(fn, 0),
): void {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/plain' }))
  const a = doc.createElement('a')
  a.href = url
  a.download = filename
  a.rel = 'noopener'
  a.style.display = 'none'
  doc.body.appendChild(a)
  a.click()
  later(() => {
    a.remove()
    URL.revokeObjectURL(url)
  })
}

// ---------------------------------------------------------------------------
// The draft across a reload
// ---------------------------------------------------------------------------

/**
 * Where the browser keeps the kitchen you were drawing.
 *
 * Namespaced and versioned, because it is a *format* rather than a value: a
 * later editor that stores a grid instead of a file has to be able to ignore
 * what this one wrote rather than parse it wrongly.
 */
export const DRAFT_KEY = 'overcooked.editor.draft.v1'

/** What survives a reload: the file, and the name it will be saved under. */
export interface StoredDraft {
  text: string
  name: string
}

/**
 * The half of `Storage` this needs, so a test can pass a `Map` and a browser
 * can pass `localStorage`.
 */
export interface DraftStore {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

/**
 * `localStorage`, when there is one that works.
 *
 * Reaching for it can *throw* rather than be absent: Safari in private browsing
 * and a Chrome profile with site data blocked both raise on access, and the
 * editor losing its draft is a worse outcome than the editor failing to mount,
 * which is what an unguarded read at module scope would do.
 */
export function browserStore(): DraftStore | null {
  try {
    const store = globalThis.localStorage
    return store ?? null
  } catch {
    return null
  }
}

/**
 * Keep the draft.
 *
 * Every keystroke writes, and that is fine: it is one small string into a
 * synchronous store, and the alternative — a debounce — is a window in which a
 * reload loses the last thing you typed, which is exactly the moment somebody
 * notices.
 */
export function saveDraft(draft: StoredDraft, store: DraftStore | null = browserStore()): void {
  if (!store) return
  try {
    store.setItem(DRAFT_KEY, JSON.stringify(draft))
  } catch {
    // A full or refused quota is not worth a broken page. The draft is still in
    // the textarea; it just will not outlive the tab.
  }
}

/**
 * The draft back, or null when there is not one.
 *
 * Anything that is not a `{ text, name }` of strings is treated as absent
 * rather than repaired: a half-understood restore would put somebody else's
 * kitchen on the screen under your name.
 */
export function loadDraft(store: DraftStore | null = browserStore()): StoredDraft | null {
  if (!store) return null
  let raw: string | null = null
  try {
    raw = store.getItem(DRAFT_KEY)
  } catch {
    return null
  }
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as Partial<StoredDraft>
    if (typeof parsed?.text !== 'string' || typeof parsed?.name !== 'string') return null
    if (parsed.text.trim().length === 0) return null
    return { text: parsed.text, name: parsed.name }
  } catch {
    return null
  }
}

/** Forget it — what "blank" and a successful save mean for the stored copy. */
export function clearDraft(store: DraftStore | null = browserStore()): void {
  if (!store) return
  try {
    store.removeItem(DRAFT_KEY)
  } catch {
    // Same reasoning as `saveDraft`.
  }
}

/** A file name a map can be saved under: the glyphs a preset name is made of. */
export function slug(name: string): string {
  const cleaned = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return cleaned || 'untitled'
}
